/**
 * @module lib/ai/assistantGemini
 * @description Asistente de cumplimiento ISO basado en Google Gemini.
 *
 * Implementación principal del asistente IA del sistema ACSGC. Usa `gemini-2.5-flash`
 * con un sistema de prompts especializado en ISO 27001/27002 y RAG para enriquecer
 * las respuestas con contexto de controles específicos.
 *
 * ## Capacidades
 * - `sendMessage()`: Chat conversacional sobre controles ISO con historial
 * - `analyzeControl()`: Análisis de brechas (gap analysis) para un control específico
 * - `suggestEvidence()`: Recomendaciones de evidencias a subir por control
 * - `suggestTasks()`: Propuestas de tareas con checklist y estimación de horas
 *
 * ## Manejo de errores de tasa
 * `retryWithBackoff()` reintenta automáticamente con backoff exponencial (1s, 2s, 4s)
 * cuando Gemini devuelve 503 (sobrecarga), 429 (rate limit) o errores de cuota.
 * Esto evita fallos por picos de carga sin necesidad de lógica de retry en los contextos.
 *
 * ## Límite diario de uso
 * Antes de cada llamada se verifica `hasReachedDailyLimit()` para no exceder
 * el umbral configurado en `geminiClient.ts`. Si se alcanza, lanza error claro.
 *
 * @see geminiClient.ts para la inicialización del cliente y control de cuota
 * @see ragSystem.ts para la construcción del contexto enriquecido
 * @see AIContext.tsx para el contexto React que expone estas funciones
 */
// =====================================================
// ASISTENTE DE CUMPLIMIENTO ISO - GEMINI VERSION
// =====================================================

import { getGeminiClient, DEFAULT_AI_CONFIG, incrementUsageCount, hasReachedDailyLimit } from './geminiClient';
import { buildEnrichedContext, getBestPractices, getRecommendedEvidenceTypes } from './ragSystem';
import type {
  AIResponse,
  EvidenceSuggestion,
  TaskSuggestion,
  GapAnalysis
} from '../../types/ai';
import type { ISOControl } from '../../types/iso';

// =====================================================
// UTILITY FUNCTIONS
// =====================================================

/**
 * Retry with exponential backoff for rate limiting and overload errors
 */
async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  initialDelay: number = 1000
): Promise<T> {
  let lastError: any;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      lastError = error;

      // Check if it's a retryable error (503, 429, rate limit)
      const isRetryable =
        error?.message?.includes('overloaded') ||
        error?.message?.includes('503') ||
        error?.message?.includes('429') ||
        error?.message?.includes('quota') ||
        error?.message?.includes('rate limit');

      if (!isRetryable || attempt === maxRetries - 1) {
        throw error;
      }

      // Exponential backoff: 1s, 2s, 4s
      const delay = initialDelay * Math.pow(2, attempt);
      console.log(`[AI] ⏳ Reintentando en ${delay}ms (intento ${attempt + 1}/${maxRetries})...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}

// =====================================================
// SYSTEM PROMPTS
// =====================================================

const SYSTEM_PROMPT = `Eres un asistente experto en ISO 27001/27002 y cumplimiento normativo de seguridad de la información.

Tu objetivo es ayudar a organizaciones a implementar y mantener controles de seguridad de manera efectiva.

Directrices:
- Proporciona respuestas precisas, prácticas y accionables
- Usa terminología ISO estándar
- Considera el contexto de la organización y el control específico
- Sugiere pasos concretos de implementación
- Cuando sea relevante, recomienda evidencias y tareas específicas
- Si no estás seguro, indícalo claramente
- Mantén un tono profesional pero accesible
- Prioriza la seguridad pero considera la usabilidad

Formato de respuesta:
- Sé conciso pero completo
- Usa listas cuando sea apropiado
- Destaca puntos críticos
- Proporciona ejemplos cuando sea útil`;

// =====================================================
// CHAT PRINCIPAL
// =====================================================

/**
 * Enviar mensaje al asistente (Gemini)
 */
export async function sendMessage(
  message: string,
  controls: ISOControl[],
  currentControl?: ISOControl,
  conversationHistory: Array<{ role: 'user' | 'assistant'; content: string }> = []
): Promise<AIResponse> {
  const client = getGeminiClient();
  if (!client) {
    throw new Error('Gemini no está configurado. Por favor, configura tu API key en Settings.');
  }

  // Verificar límite diario
  if (hasReachedDailyLimit()) {
    throw new Error('Has alcanzado el límite diario de consultas gratuitas. Intenta mañana o configura tu propia API key premium.');
  }

  try {
    console.log('[AI] 💬 Enviando mensaje a Gemini:', message.substring(0, 50) + '...');

    // Construir contexto enriquecido con RAG
    const enrichedContext = buildEnrichedContext(message, controls, currentControl);

    // Preparar el prompt completo
    const fullPrompt = `${SYSTEM_PROMPT}

Contexto adicional:
${enrichedContext}

Historial de conversación:
${conversationHistory.map(msg => `${msg.role}: ${msg.content}`).join('\n')}

Usuario: ${message}

Asistente:`;

    // Llamar a Gemini con reintentos automáticos
    const model = client.getGenerativeModel({
      model: DEFAULT_AI_CONFIG.model,
      generationConfig: {
        temperature: DEFAULT_AI_CONFIG.temperature,
        maxOutputTokens: DEFAULT_AI_CONFIG.maxTokens,
      }
    });

    const assistantMessage = await retryWithBackoff(async () => {
      const result = await model.generateContent(fullPrompt);
      const response = await result.response;
      return response.text();
    });

    // Incrementar contador de uso después de una llamada exitosa
    incrementUsageCount();

    console.log('[AI] ✅ Respuesta recibida de Gemini:', assistantMessage.substring(0, 100) + '...');

    return {
      message: assistantMessage,
      confidence: 90,
      metadata: {
        model: DEFAULT_AI_CONFIG.model,
        provider: 'gemini'
      }
    };
  } catch (error: any) {
    console.error('[AI] ❌ Error en sendMessage:', error);

    // Mensajes de error específicos
    if (error?.message?.includes('API key')) {
      throw new Error('API key inválida. Por favor, verifica tu configuración.');
    } else if (error?.message?.includes('quota')) {
      throw new Error('Cuota excedida. Por favor, verifica tu cuenta de Google Cloud.');
    }

    throw new Error(error?.message || 'Error al comunicarse con el asistente de IA');
  }
}

// =====================================================
// SUGERENCIAS DE EVIDENCIAS
// =====================================================

/**
 * Sugerir evidencias apropiadas para un control (Gemini)
 */
export async function suggestEvidence(control: ISOControl): Promise<EvidenceSuggestion[]> {
  const client = getGeminiClient();
  if (!client) {
    return getEvidenceSuggestionsFromRules(control);
  }

  try {
    console.log('[AI] 📄 Sugiriendo evidencias con Gemini para:', control.code);

    const prompt = `Necesito recomendaciones de evidencias específicas para el siguiente control ISO 27001/27002:

Control: ${control.code} - ${control.title}
Descripción: ${control.description}
Categoría: ${control.category}
Subcategoría: ${control.subcategory}

Por favor, sugiere 5 tipos de evidencias concretas que demuestren el cumplimiento de este control.
Para cada evidencia incluye:
1. Nombre descriptivo
2. Descripción breve (1-2 líneas)
3. Por qué es importante esta evidencia
4. Prioridad (high/medium/low)
5. Ejemplos específicos (opcional)

Responde en formato JSON:
{
  "evidences": [
    {
      "name": "...",
      "description": "...",
      "reasoning": "...",
      "priority": "high|medium|low",
      "examples": ["...", "..."]
    }
  ]
}`;

    const model = client.getGenerativeModel({
      model: DEFAULT_AI_CONFIG.model,
      generationConfig: {
        temperature: 0.7,
        maxOutputTokens: 2048,
      }
    });

    const content = await retryWithBackoff(async () => {
      const result = await model.generateContent(prompt);
      const response = await result.response;
      return response.text();
    });

    // Extraer JSON de la respuesta (Gemini a veces incluye ```json```)
    const jsonMatch = content.match(/```json\n([\s\S]*?)\n```/) || content.match(/\{[\s\S]*\}/);
    const jsonStr = jsonMatch ? (jsonMatch[1] || jsonMatch[0]) : content;
    const parsed = JSON.parse(jsonStr);

    const suggestions: EvidenceSuggestion[] = (parsed.evidences || []).map((ev: any, idx: number) => ({
      id: `ev-${control.id}-${idx}`,
      type: determineEvidenceType(ev.name),
      name: ev.name,
      description: ev.description,
      reasoning: ev.reasoning,
      priority: ev.priority || 'medium',
      examples: ev.examples || [],
      tags: [control.category, control.subcategory]
    }));

    console.log('[AI] ✅ Generadas', suggestions.length, 'sugerencias de evidencia con Gemini');
    return suggestions;

  } catch (error) {
    console.error('[AI] ❌ Error sugiriendo evidencias con Gemini, usando fallback:', error);
    return getEvidenceSuggestionsFromRules(control);
  }
}

/**
 * Fallback: sugerencias basadas en reglas
 */
function getEvidenceSuggestionsFromRules(control: ISOControl): EvidenceSuggestion[] {
  const recommendedTypes = getRecommendedEvidenceTypes(control.category);

  return recommendedTypes.slice(0, 5).map((type, idx) => ({
    id: `ev-fallback-${control.id}-${idx}`,
    type: determineEvidenceType(type),
    name: type,
    description: `Evidencia necesaria para demostrar cumplimiento del control ${control.code}`,
    reasoning: `Requerido para la categoría ${control.category}`,
    priority: idx === 0 ? 'high' : idx === 1 ? 'medium' : 'low',
    examples: [],
    tags: [control.category]
  }));
}

function determineEvidenceType(name: string): 'document' | 'screenshot' | 'video' | 'other' {
  const nameLower = name.toLowerCase();
  if (nameLower.includes('captura') || nameLower.includes('screenshot') || nameLower.includes('pantalla')) {
    return 'screenshot';
  }
  if (nameLower.includes('video') || nameLower.includes('grabación')) {
    return 'video';
  }
  if (nameLower.includes('documento') || nameLower.includes('política') || nameLower.includes('procedimiento')) {
    return 'document';
  }
  return 'other';
}

// =====================================================
// SUGERENCIAS DE TAREAS
// =====================================================

/**
 * Sugerir tareas para implementar un control (Gemini)
 */
export async function suggestTasks(control: ISOControl): Promise<TaskSuggestion[]> {
  const client = getGeminiClient();
  if (!client) {
    return getTaskSuggestionsFromRules(control);
  }

  try {
    console.log('[AI] 📋 Sugiriendo tareas con Gemini para:', control.code);

    const prompt = `Necesito un plan de implementación detallado para el siguiente control ISO 27001/27002:

Control: ${control.code} - ${control.title}
Descripción: ${control.description}
Categoría: ${control.category}
Estado actual: ${control.status}
Prioridad: ${control.priority}

Por favor, sugiere 3-5 tareas concretas para implementar este control.
Para cada tarea incluye:
1. Título claro y accionable
2. Descripción detallada
3. Prioridad (low/medium/high/critical)
4. Estimación de horas
5. Checklist de subtareas (3-5 items)
6. Justificación de por qué es necesaria

Responde en formato JSON:
{
  "tasks": [
    {
      "title": "...",
      "description": "...",
      "priority": "low|medium|high|critical",
      "estimatedHours": number,
      "checklist": [
        { "text": "...", "required": true|false }
      ],
      "reasoning": "..."
    }
  ]
}`;

    const model = client.getGenerativeModel({
      model: DEFAULT_AI_CONFIG.model,
      generationConfig: {
        temperature: 0.7,
        maxOutputTokens: 2048,
      }
    });

    const content = await retryWithBackoff(async () => {
      const result = await model.generateContent(prompt);
      const response = await result.response;
      return response.text();
    });

    const jsonMatch = content.match(/```json\n([\s\S]*?)\n```/) || content.match(/\{[\s\S]*\}/);
    const jsonStr = jsonMatch ? (jsonMatch[1] || jsonMatch[0]) : content;
    const parsed = JSON.parse(jsonStr);

    const suggestions: TaskSuggestion[] = (parsed.tasks || []).map((task: any, idx: number) => ({
      id: `task-${control.id}-${idx}`,
      title: task.title,
      description: task.description,
      priority: task.priority || 'medium',
      estimatedHours: task.estimatedHours || 8,
      checklist: task.checklist || [],
      reasoning: task.reasoning,
      tags: [control.category, control.code]
    }));

    console.log('[AI] ✅ Generadas', suggestions.length, 'sugerencias de tareas con Gemini');
    return suggestions;

  } catch (error) {
    console.error('[AI] ❌ Error sugiriendo tareas con Gemini, usando fallback:', error);
    return getTaskSuggestionsFromRules(control);
  }
}

function getTaskSuggestionsFromRules(control: ISOControl): TaskSuggestion[] {
  const bestPractices = getBestPractices(control.category);

  return bestPractices.slice(0, 3).map((practice, idx) => ({
    id: `task-fallback-${control.id}-${idx}`,
    title: practice,
    description: `Implementar: ${practice} para el control ${control.code}`,
    priority: idx === 0 ? 'high' : 'medium',
    estimatedHours: 8,
    checklist: [
      { text: 'Revisar requisitos del control', required: true },
      { text: 'Documentar procedimiento', required: true },
      { text: 'Implementar control', required: true },
      { text: 'Recopilar evidencias', required: true },
      { text: 'Revisar y validar', required: false }
    ],
    reasoning: `Mejor práctica recomendada para ${control.category}`,
    tags: [control.category]
  }));
}

// =====================================================
// ANÁLISIS DE GAPS
// =====================================================

/**
 * Analizar gaps de cumplimiento en un control (Gemini)
 */
export async function analyzeGaps(control: ISOControl): Promise<GapAnalysis> {
  const client = getGeminiClient();
  if (!client) {
    return getGapAnalysisFromRules(control);
  }

  try {
    console.log('[AI] 🔍 Analizando gaps con Gemini para:', control.code);

    const prompt = `Analiza el estado de cumplimiento del siguiente control ISO 27001/27002:

Control: ${control.code} - ${control.title}
Descripción: ${control.description}
Estado actual: ${control.status}
Prioridad: ${control.priority}
Evidencias existentes: ${control.evidence.length}
Tareas completadas: ${control.tasks.filter(t => t.status === 'completed').length}/${control.tasks.length}

Proporciona un análisis de gaps detallado incluyendo:
1. Score general de cumplimiento (0-100)
2. Gaps identificados por categoría
3. Recomendaciones priorizadas
4. Estimación de esfuerzo
5. Nivel de riesgo

Responde en formato JSON:
{
  "overallScore": number,
  "gaps": [
    {
      "category": "documentation|implementation|evidence|process|technical",
      "title": "...",
      "description": "...",
      "severity": "low|medium|high|critical",
      "currentState": "...",
      "desiredState": "..."
    }
  ],
  "recommendations": [
    {
      "title": "...",
      "description": "...",
      "priority": "low|medium|high|critical",
      "impact": "...",
      "effort": "low|medium|high",
      "quickWin": true|false
    }
  ],
  "estimatedEffort": { "hours": number, "complexity": "low|medium|high" },
  "riskLevel": "low|medium|high|critical"
}`;

    const model = client.getGenerativeModel({
      model: DEFAULT_AI_CONFIG.model,
      generationConfig: {
        temperature: 0.6,
        maxOutputTokens: 2048,
      }
    });

    const content = await retryWithBackoff(async () => {
      const result = await model.generateContent(prompt);
      const response = await result.response;
      return response.text();
    });

    const jsonMatch = content.match(/```json\n([\s\S]*?)\n```/) || content.match(/\{[\s\S]*\}/);
    const jsonStr = jsonMatch ? (jsonMatch[1] || jsonMatch[0]) : content;
    const parsed = JSON.parse(jsonStr);

    const analysis: GapAnalysis = {
      controlId: control.id,
      overallScore: parsed.overallScore || 0,
      gaps: (parsed.gaps || []).map((gap: any, idx: number) => ({
        id: `gap-${control.id}-${idx}`,
        ...gap
      })),
      recommendations: (parsed.recommendations || []).map((rec: any, idx: number) => ({
        id: `rec-${control.id}-${idx}`,
        ...rec
      })),
      estimatedEffort: parsed.estimatedEffort || { hours: 0, complexity: 'medium' },
      riskLevel: parsed.riskLevel || 'medium'
    };

    console.log('[AI] ✅ Análisis de gaps completado con Gemini. Score:', analysis.overallScore);
    return analysis;

  } catch (error) {
    console.error('[AI] ❌ Error analizando gaps con Gemini, usando fallback:', error);
    return getGapAnalysisFromRules(control);
  }
}

function getGapAnalysisFromRules(control: ISOControl): GapAnalysis {
  let score = 0;

  if (control.status === 'completed') score += 40;
  else if (control.status === 'review') score += 30;
  else if (control.status === 'in_progress') score += 20;

  if (control.evidence.length > 0) score += 30;
  if (control.tasks.length > 0) score += 15;

  const completedTasks = control.tasks.filter(t => t.status === 'completed').length;
  if (control.tasks.length > 0) {
    score += (completedTasks / control.tasks.length) * 15;
  }

  const gaps = [];
  if (control.evidence.length === 0) {
    gaps.push({
      id: 'gap-evidence',
      category: 'evidence' as const,
      title: 'Falta de evidencias',
      description: 'No se han cargado evidencias de cumplimiento',
      severity: 'high' as const,
      currentState: 'Sin evidencias',
      desiredState: 'Al menos 2-3 evidencias documentadas'
    });
  }

  const recommendations = [
    {
      id: 'rec-1',
      title: 'Documentar procedimientos',
      description: 'Crear documentación formal de los procedimientos del control',
      priority: 'high' as const,
      impact: 'Mejora significativa en auditorías',
      effort: 'medium' as const,
      quickWin: false
    }
  ];

  return {
    controlId: control.id,
    overallScore: Math.round(score),
    gaps,
    recommendations,
    estimatedEffort: { hours: 16, complexity: 'medium' },
    riskLevel: score < 30 ? 'high' : score < 60 ? 'medium' : 'low'
  };
}
